/**
 * Lake Charles Home Page
 *
 * Main landing page with all sections assembled.
 * Mobile performance optimizations:
 * - Lazy load below-fold sections to reduce initial bundle size
 * - Critical above-fold content loads immediately
 */

import React, { lazy, Suspense } from 'react';
import { SEOHead } from '@/components/shared/seo/SEOHead';
import { SEOPresets } from '@/components/shared/seo/seoUtils';
import {
  HeroSection,
  FeatureSection,
  InterestForm,
  createApiClient,
  QuickNavigation,
  ProblemSection,
  PricingSection,
  ChampionsSection,
} from '@core';
import { submitInterestForm } from '../services/interestFormService';
import lakeCharlesConfig from '../city.config';
import { lakeCharlesFeatures } from '../data/features';
import { lakeCharlesVideoLibrary } from '../data/videoLibrary';

// Lazy load below-fold sections (mobile performance optimization)
const VideoLibrarySection = lazy(() =>
  import('@core/components/VideoLibrarySection').then((m) => ({ default: m.VideoLibrarySection })),
);
const CommunityTreasurySection = lazy(() =>
  import('@core/components/CommunityTreasurySection').then((m) => ({
    default: m.CommunityTreasurySection,
  })),
);
const LegacyComparison = lazy(() =>
  import('@core/components/LegacyComparison').then((m) => ({ default: m.LegacyComparison })),
);
const DeploymentPathwaySection = lazy(() =>
  import('@core/components/DeploymentPathwaySection').then((m) => ({
    default: m.DeploymentPathwaySection,
  })),
);
const ChampionsFAQSection = lazy(() =>
  import('@core/components/ChampionsFAQSection').then((m) => ({ default: m.ChampionsFAQSection })),
);

export function LakeCharlesHomePage() {
  const apiClient = createApiClient(lakeCharlesConfig.api);

  const scrollToForm = () => {
    const formElement = document.getElementById('interest-form');
    if (formElement) {
      formElement.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <>
      <SEOHead {...SEOPresets.home('lakecharles')} />

      {/* Quick Navigation Sidebar */}
      <QuickNavigation />

      {/* Hero Section */}
      <HeroSection
        cityName={lakeCharlesConfig.identity.cityName}
        stateName={lakeCharlesConfig.identity.stateName}
        badgeText={lakeCharlesConfig.content.hero.badgeText}
        headline={lakeCharlesConfig.content.hero.headline}
        subheadline={lakeCharlesConfig.content.hero.subheadline}
        bodyText={lakeCharlesConfig.content.hero.bodyText}
        primaryCta={lakeCharlesConfig.content.hero.primaryCta}
        secondaryCta={lakeCharlesConfig.content.hero.secondaryCta}
        heroImage={lakeCharlesConfig.content.hero.heroImage}
        enableScrollAnimation={lakeCharlesConfig.features.enableScrollAnimation}
        animationIntensity={lakeCharlesConfig.features.animationIntensity}
      />

      {/* Problem Section */}
      <ProblemSection
        cityName={lakeCharlesConfig.identity.cityName}
        paragraphs={[
          `Lake Charles residents know the frustration. Decisions that affect your neighborhood, your wallet, and your future are made without your real input. A City Council vote can reshape the community, but what if the majority of residents disagree? Where do you turn?`,
          `Traditional tools like Facebook comments, petitions that disappear, or neighborhood gossip aren't enough. You need something better. Something organized. Something powerful.`,
        ]}
      />

      {/* Features Section */}
      <FeatureSection
        cityName={lakeCharlesConfig.identity.cityName}
        title={lakeCharlesConfig.content.features.title}
        subtitle={lakeCharlesConfig.content.features.subtitle}
        features={lakeCharlesFeatures}
        enableVideoDemos={lakeCharlesConfig.features.enableVideoDemos}
      />

      {/* Pricing Section */}
      <PricingSection
        cityName={lakeCharlesConfig.identity.cityName}
        populationDisplay="All 85,000 residents"
        mayorPhoneNumber="(337) 491-1201"
        mayorPhoneTel="+13374911201"
        mayorPhoneLabel="Lake Charles City Hall"
      />

      {/* Champions Section (includes How It Works) */}
      <ChampionsSection
        cityName={lakeCharlesConfig.identity.cityName}
        applicationUrl="/champions/apply"
      />

      {/* Below-fold sections - Lazy loaded for mobile performance */}
      <Suspense
        fallback={
          <div className="min-h-[400px] flex items-center justify-center">
            <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
          </div>
        }
      >
        <VideoLibrarySection
          videos={lakeCharlesVideoLibrary}
          cityName={lakeCharlesConfig.identity.cityName}
        />
      </Suspense>

      <Suspense fallback={<div className="min-h-[400px]" />}>
        <CommunityTreasurySection
          cityName={lakeCharlesConfig.identity.cityName}
          stateName={lakeCharlesConfig.identity.stateName}
          annualSpending={(lakeCharlesConfig as any).economics.annualConsumerSpending}
          vendorsPageUrl="/vendors"
        />
      </Suspense>

      <Suspense fallback={<div className="min-h-[300px]" />}>
        <LegacyComparison cityName={lakeCharlesConfig.identity.cityName} />
      </Suspense>

      <Suspense fallback={<div className="min-h-[400px]" />}>
        <DeploymentPathwaySection cityName={lakeCharlesConfig.identity.cityName} />
      </Suspense>

      <Suspense fallback={<div className="min-h-[500px]" />}>
        <ChampionsFAQSection
          cityName={lakeCharlesConfig.identity.cityName}
          applicationUrl="/champions/apply"
          contactEmail="" /* "champions@toolboxlogic.com" */
        />
      </Suspense>

      {/* Interest Form */}
      <InterestForm
        cityName={lakeCharlesConfig.identity.cityName}
        stateName={lakeCharlesConfig.identity.stateName}
        apiClient={apiClient}
        onSubmit={async (data) => {
          const res = await submitInterestForm(data);
          return { success: res.success, error: res.error, data: res.data };
        }}
        title={lakeCharlesConfig.content.interestForm.title}
        description={lakeCharlesConfig.content.interestForm.description}
        successMessage={lakeCharlesConfig.content.interestForm.successMessage}
        enableChampionProgram={lakeCharlesConfig.features.enableChampionProgram}
        enablePlanSelector={lakeCharlesConfig.features.enablePlanSelector}
        enableFacebookGroups={lakeCharlesConfig.features.enableFacebookGroups}
        championApplicationUrl="/champions/apply"
        termsUrl="/terms"
        privacyUrl="/privacy"
      />

      <QuickNavigation />
    </>
  );
}
